import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { 
  Shield, Grid, BarChart3, MapPin, Bell, Menu,
  Calendar, AlertTriangle, Users, UserPlus, Building, Settings,
  Smartphone, LogOut, FileSpreadsheet, TrendingUp, Activity, X, ClipboardCheck,
  ArrowLeftRight
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';

const menuItems = [
  { section: 'OVERVIEW', items: [
    { icon: Grid, label: 'Dashboard', path: '/admin' },
    { icon: TrendingUp, label: 'Analytics', path: '/admin/analytics' },
    { icon: Activity, label: 'Activity Timeline', path: '/admin/activity' },
    { icon: MapPin, label: 'Live Tracking', path: '/admin/tracking' },
  ]},
  { section: 'MANAGEMENT', items: [
    { icon: Calendar, label: 'Schedules', path: '/admin/schedules' },
    { icon: FileSpreadsheet, label: 'Timesheets', path: '/admin/timesheets' },
    { icon: ClipboardCheck, label: 'Approvals', path: '/admin/approvals', badge: 'approvals' },
    { icon: AlertTriangle, label: 'Incidents', path: '/admin/incidents' },
    { icon: Users, label: 'Users', path: '/admin/users' },
    { icon: UserPlus, label: 'Join Requests', path: '/admin/join-requests', badge: true },
    { icon: Building, label: 'Sites', path: '/admin/sites' },
    { icon: Settings, label: 'Organization', path: '/admin/organization' },
  ]},
];

export default function AdminLayout({ children }) {
  const { user, logout, viewMode, toggleViewMode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);

  useEffect(() => {
    loadNotifications();
    loadPendingApprovalsCount();
    const interval = setInterval(() => {
      loadNotifications();
      loadPendingApprovalsCount();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close sidebar when route changes on mobile
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const loadNotifications = async () => {
    try {
      const data = await api.getNotifications();
      setNotifications(Array.isArray(data) ? data.slice(0, 10) : []);
      setUnreadCount(Array.isArray(data) ? data.filter(n => !n.read).length : 0);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const loadPendingApprovalsCount = async () => {
    try {
      const data = await api.getPendingApprovalsCount();
      setPendingApprovalsCount(data?.count || 0);
    } catch (error) {
      console.error('Error loading pending approvals count:', error);
    }
  };

  const markAsRead = async (id) => {
    try {
      await api.markNotificationRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  const getRoleBadge = (role) => {
    const labels = { super_admin: 'Super Admin', admin: 'Admin', supervisor: 'Supervisor' };
    return labels[role] || role;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-layout">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 z-40">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 -ml-2 rounded-lg hover:bg-slate-100"
        >
          <Menu className="w-5 h-5 text-slate-700" />
        </button>
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-700" />
          <span className="font-bold text-slate-800 text-sm">Admin Panel</span>
        </div>
        <div className="flex items-center gap-1">
          {user?.role === 'supervisor' && (
            <button
              onClick={() => { toggleViewMode(); navigate('/dashboard'); }}
              className="p-2 rounded-lg hover:bg-slate-100"
              title="Switch to Field Mode"
              data-testid="mobile-switch-field-btn"
            >
              <ArrowLeftRight className="w-4 h-4 text-emerald-600" />
            </button>
          )}
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative p-2 -mr-2 rounded-lg hover:bg-slate-100"
          >
            <Bell className="w-5 h-5 text-slate-600" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
        </div>
      </header>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-white border-r border-slate-200 z-50
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Brand */}
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-blue-700" />
              </div>
              <div>
                <p className="font-bold text-slate-800 text-sm">TheSecurityGuardApp</p>
                <p className="text-xs text-slate-500">Admin Panel</p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 rounded hover:bg-slate-100"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
          {/* User Badge */}
          <div className="mt-3 p-2 bg-slate-50 rounded-lg flex items-center gap-2">
            <div className="w-6 h-6 bg-blue-700 rounded-full flex items-center justify-center">
              <span className="text-xs font-bold text-white">
                {user?.name?.[0]?.toUpperCase()}
              </span>
            </div>
            <span className="text-sm font-medium text-slate-700 flex-1 truncate">{user?.name}</span>
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-medium">
              {getRoleBadge(user?.role)}
            </span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-3" style={{ maxHeight: 'calc(100vh - 180px)' }}>
          {menuItems.map((section) => (
            <div key={section.section} className="mb-4">
              <p className="text-xs font-semibold text-slate-400 px-3 mb-2">{section.section}</p>
              {section.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-colors ${
                    isActive(item.path)
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  <span className="text-sm font-medium flex-1">{item.label}</span>
                  {item.badge === 'approvals' && pendingApprovalsCount > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                      {pendingApprovalsCount > 99 ? '99+' : pendingApprovalsCount}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-slate-200 bg-white">
          <div className="flex gap-2">
            {user?.role === 'supervisor' && (
              <button
                onClick={() => { 
                  toggleViewMode(); 
                  navigate('/dashboard'); 
                  setSidebarOpen(false); 
                }}
                className="flex-1 flex items-center justify-center gap-2 py-2 px-3 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-medium hover:bg-emerald-100 transition-colors"
                data-testid="switch-to-field-btn"
              >
                <ArrowLeftRight className="w-4 h-4" />
                Field Mode
              </button>
            )}
            <button
              onClick={handleLogout}
              className="flex-1 flex items-center justify-center gap-2 py-2 px-3 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-14 lg:pt-0 min-h-screen">
        {/* Desktop Header */}
        <div className="hidden lg:flex justify-end p-4 pb-0">
          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
              data-testid="notification-bell"
            >
              <Bell className="w-5 h-5 text-slate-600" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Notification Dropdown */}
        {showNotifications && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setShowNotifications(false)} />
            <div className="fixed right-4 top-14 lg:top-16 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-xl border border-slate-200 z-50">
              <div className="flex items-center justify-between p-3 border-b border-slate-100">
                <h3 className="font-semibold text-slate-800">Notifications</h3>
                <button onClick={() => setShowNotifications(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="p-6 text-center text-slate-400">
                    <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No notifications</p>
                  </div>
                ) : (
                  notifications.map((notif) => (
                    <div
                      key={notif.id}
                      onClick={() => {
                        markAsRead(notif.id);
                        if (notif.type === 'join_request') {
                          navigate('/admin/join-requests');
                          setShowNotifications(false);
                        }
                      }}
                      className={`p-3 border-b border-slate-50 cursor-pointer hover:bg-slate-50 transition-colors ${
                        !notif.read ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          notif.type === 'join_request' ? 'bg-purple-100' :
                          notif.type === 'incident' ? 'bg-red-100' : 'bg-blue-100'
                        }`}>
                          {notif.type === 'join_request' ? (
                            <UserPlus className="w-4 h-4 text-purple-600" />
                          ) : notif.type === 'incident' ? (
                            <AlertTriangle className="w-4 h-4 text-red-600" />
                          ) : (
                            <Bell className="w-4 h-4 text-blue-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${!notif.read ? 'font-semibold text-slate-800' : 'text-slate-700'}`}>
                            {notif.title}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5 truncate">{notif.message}</p>
                        </div>
                        {!notif.read && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}

        <div className="p-4 lg:p-6">
          {children}
        </div>
      </main>
    </div>
  );
}
